"use client"

import { Box, HStack, Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import MarkdownParser from "@/components/MarkdownParser"
import { ClipboardIconButton, ClipboardRoot } from "@/components/ui/clipboard"
import { Tooltip } from "@/components/ui/tooltip"
import { type ColorPaletteColumnsBlock as ColorPaletteColumnsBlockType } from "@/types/database"
import getContrastRatio from "@/utils/getContrastRatio"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface ColorPaletteColumnsBlockProps extends ContentBlockTitleNumberProps {
  block: ColorPaletteColumnsBlockType
}

type PaletteColor =
  ColorPaletteColumnsBlockType["column_palette"][number]["color_swatch"][number] & {
    description?: string
  }
type PaletteColumn = Omit<
  ColorPaletteColumnsBlockType["column_palette"][number],
  "color_swatch"
> & {
  color_swatch: PaletteColor[]
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const normalizePaletteColor = (color: unknown): PaletteColor | null => {
  if (!isRecord(color)) return null

  const name = color.name
  const hex = color.hex

  if (typeof name !== "string" || typeof hex !== "string") {
    return null
  }

  return {
    contrast_ratio:
      typeof color.contrast_ratio === "string"
        ? color.contrast_ratio
        : getContrastRatio(hex),
    darker_bg: Boolean(color.darker_bg),
    description:
      typeof color.description === "string" ? color.description : undefined,
    hex,
    name,
  }
}

const parseColorSwatch = (value: unknown): PaletteColor[] => {
  const parsedValue =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as unknown
          } catch {
            return value
          }
        })()
      : value

  if (!Array.isArray(parsedValue)) {
    return []
  }

  return parsedValue.reduce<PaletteColor[]>((colors, color) => {
    const normalizedColor = normalizePaletteColor(color)

    if (normalizedColor !== null) {
      colors.push(normalizedColor)
    }

    return colors
  }, [])
}

const normalizePaletteColumn = (column: unknown): PaletteColumn | null => {
  if (!isRecord(column)) return null

  const color_swatch = parseColorSwatch(column.color_swatch)
  const column_name =
    typeof column.column_name === "string" ? column.column_name : undefined
  const description =
    typeof column.description === "string" ? column.description : undefined

  if (!column_name && !description && color_swatch.length === 0) {
    return null
  }

  return {
    color_swatch,
    column_name,
    description,
  }
}

const normalizePaletteColumns = (value: unknown): PaletteColumn[] => {
  const columns = Array.isArray(value) ? value : [value]

  return columns.reduce<PaletteColumn[]>((normalizedColumns, column) => {
    const normalizedColumn = normalizePaletteColumn(column)

    if (normalizedColumn !== null) {
      normalizedColumns.push(normalizedColumn)
    }

    return normalizedColumns
  }, [])
}

const parseJsonCodeValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value
  }

  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

const ColorPaletteColumnsBlock: FC<ColorPaletteColumnsBlockProps> = ({
  block,
  titleNumber,
}) => {
  const {
    column_palette,
    darker_bg,
    description,
    json_code,
    legend,
    subtitle,
    title,
    two_columns_palette,
  } = block

  const trimmedTitle = title?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription =
    description !== undefined || description !== "" || description !== null
  const hasSubtitle =
    subtitle !== undefined || subtitle !== "" || subtitle !== null
  const copy = subtitle ?? description ?? ""
  let all_column_palette = normalizePaletteColumns(column_palette)

  if (json_code) {
    try {
      const parsed = parseJsonCodeValue(json_code)
      all_column_palette = normalizePaletteColumns(parsed)

      if (!Array.isArray(parsed) && isRecord(parsed)) {
        all_column_palette = Object.entries(parsed).map(
          ([columnName, shades]) => {
            const color_swatch: PaletteColor[] = Object.entries(
              shades as Record<string, unknown>
            ).reduce<PaletteColor[]>((swatches, [shadeName, shadeValue]) => {
              const hex =
                typeof shadeValue === "string"
                  ? shadeValue
                  : (shadeValue as { value?: string })?.value

              if (!hex || typeof hex !== "string") {
                return swatches
              }

              swatches.push({
                contrast_ratio: getContrastRatio(hex),
                darker_bg: false,
                description:
                  typeof (shadeValue as { description?: unknown })
                    ?.description === "string"
                    ? ((shadeValue as { description?: string }).description ??
                      undefined)
                    : undefined,
                hex,
                name: shadeName,
              })

              return swatches
            }, [])

            const columnDescriptionRaw = (shades as { description?: unknown })
              ?.description
            const normalizedDescription =
              typeof columnDescriptionRaw === "string"
                ? columnDescriptionRaw
                : typeof (columnDescriptionRaw as { value?: unknown })
                      ?.value === "string"
                  ? ((columnDescriptionRaw as { value?: string }).value ??
                    undefined)
                  : undefined

            return {
              color_swatch: color_swatch.filter(
                (swatch) => swatch.name.toLowerCase() !== "description"
              ),
              column_name: columnName,
              description: normalizedDescription,
            }
          }
        )
      }
    } catch (error) {
      console.error(
        "Unable to normalize json_code in ColorPaletteColumnsBlock:",
        error
      )
    }
  }

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="color-palette-columns-block internal-padding"
      direction={{ base: "column", lg: two_columns_palette ? "row" : "column" }}
      gap={{ base: 8, lg: two_columns_palette ? 6 : 8 }}
    >
      {hasTitle && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: block.two_columns_palette
              ? block.title !== undefined
                ? "30%"
                : "100%"
              : "100%",
          }}
          pb={8}
          {...getStickyContentBlockLeftProps(two_columns_palette)}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle
              fontWeight="bold"
              title={trimmedTitle}
              titleNumber={titleNumber}
            />
            {hasSubtitle && (
              <Text as="p" color={"fg.muted"} fontSize="lg">
                {subtitle}
              </Text>
            )}
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={copy}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: block.two_columns_palette
            ? block.title !== undefined
              ? "70%"
              : "100%"
            : "100%",
        }}
        pl={!hasTitle && two_columns_palette ? { base: 0, lg: 12 } : 0}
      >
        <BlurFade inView>
          <HStack
            alignItems="flex-start"
            bgColor="bg.subtle"
            borderColor="border"
            borderRadius="3xl"
            borderWidth="1px"
            columnGap={4}
            p={6}
            rowGap={8}
            w="100%"
            wrap="wrap"
          >
            {all_column_palette.map((column) => (
              <VStack
                alignItems="flex-start"
                gap={0}
                key={
                  column.column_name ??
                  `column-${column.description ?? "palette"}`
                }
              >
                {column.column_name && (
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    pb={4}
                    textTransform={"capitalize"}
                  >
                    {column.column_name}
                  </Text>
                )}

                <VStack
                  borderRadius="lg"
                  boxShadow="md"
                  gap={0}
                  overflow="hidden"
                  w="100%"
                >
                  {column.color_swatch.map((color) => {
                    const swatchContent = (
                      <Box
                        alignItems="center"
                        bg={color.hex}
                        color={
                          parseFloat(color.contrast_ratio) >= 4.5
                            ? "whiteAlpha.900"
                            : "blackAlpha.900"
                        }
                        display="flex"
                        gap={3}
                        justifyContent="space-between"
                        px={3}
                        py={2}
                        w="100%"
                      >
                        <Text
                          color="currentColor"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          {color.name}
                        </Text>
                        <HStack gap={1} minW={0}>
                          <Text
                            color="currentColor"
                            fontFamily="mono"
                            fontSize="xs"
                            lineClamp={1}
                          >
                            {color.hex}
                          </Text>
                          <ClipboardRoot timeout={1200} value={color.hex}>
                            <ClipboardIconButton
                              aria-label={`Copy ${color.name} value`}
                              color="currentColor"
                              minW="6"
                              opacity={0.72}
                              size="2xs"
                              variant="plain"
                            />
                          </ClipboardRoot>
                        </HStack>
                      </Box>
                    )

                    return (
                      <HStack
                        _hover={{
                          transform: "scale(1.05)",
                        }}
                        alignItems="stretch"
                        key={`${column.column_name ?? "column"}-${color.name}-${color.hex}`}
                        minH="40px"
                        transition="transform 0.2s ease-in-out"
                        w="100%"
                      >
                        {color.description ? (
                          <Tooltip content={color.description} showArrow>
                            {swatchContent}
                          </Tooltip>
                        ) : (
                          swatchContent
                        )}
                      </HStack>
                    )
                  })}
                </VStack>
              </VStack>
            ))}
          </HStack>
        </BlurFade>

        {legend && (
          <BlurFade flexDirection="column" inView>
            <MarkdownParser fontSize="lg" markdown={legend} />
          </BlurFade>
        )}
      </VStack>
    </Stack>
  )
}

export default ColorPaletteColumnsBlock
